import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import PurchaseCreditsWidget from '../components/Account/PurchaseCreditsWidget';
import { useAuth } from '../contexts/AuthContext';
import { useAccountApi } from '../utils/accountApi';
import './AccountPage.css';

function formatCurrency(amount, currency = 'USD', locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount || 0);
}

function formatDate(value, locale = 'en-US') {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Pending', value: 'pending' },
  { label: 'Failed', value: 'failed' },
];

const SORT_OPTIONS = [
  { label: 'Newest first', value: 'created_at_desc' },
  { label: 'Oldest first', value: 'created_at_asc' },
];

function AccountPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const accountApi = useAccountApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('created_at_desc');

  const paymentSessionId = searchParams.get('session_id');
  const paymentResult = searchParams.get('payment');

  const currentLocale = navigator.language || 'en-US';

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isAuthenticated) {
        return;
      }
      setLoading(true);
      setError('');
      try {
        const [accountPayload, txPayload] = await Promise.all([
          accountApi.getAccount(),
          accountApi.getTransactions({ page: 1, page_size: 10, sort: sortOrder, status: statusFilter }),
        ]);

        if (!cancelled) {
          setAccount(accountPayload);
          setTransactions(txPayload.items || []);
          setNextToken(txPayload.next_token || null);
          setHistoryPage(1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load account data.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [accountApi, isAuthenticated, sortOrder, statusFilter]);

  useEffect(() => {
    let cancelled = false;

    async function maybeConfirmPayment() {
      if (!isAuthenticated || paymentResult !== 'success' || !paymentSessionId) {
        return;
      }

      try {
        const confirmPayload = await accountApi.confirmPayment({
          sessionId: paymentSessionId,
          idempotencyKey: `confirm-${paymentSessionId}`,
        });

        if (cancelled) {
          return;
        }

        if (confirmPayload.status === 'succeeded') {
          setReceipt(confirmPayload.receipt);
          const accountPayload = await accountApi.getAccount();
          const txPayload = await accountApi.getTransactions({
            page: 1,
            page_size: 10,
            sort: sortOrder,
            status: statusFilter,
          });
          if (!cancelled) {
            setAccount(accountPayload);
            setTransactions(txPayload.items || []);
            setNextToken(txPayload.next_token || null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Payment confirmation failed.');
        }
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('payment');
          next.delete('session_id');
          setSearchParams(next, { replace: true });
        }
      }
    }

    maybeConfirmPayment();

    return () => {
      cancelled = true;
    };
  }, [
    accountApi,
    isAuthenticated,
    paymentResult,
    paymentSessionId,
    searchParams,
    setSearchParams,
    sortOrder,
    statusFilter,
  ]);

  const visibleTransactions = useMemo(() => transactions || [], [transactions]);

  async function handlePurchase({ bundleId, idempotencyKey }) {
    setPurchaseLoading(true);
    setError('');
    try {
      const response = await accountApi.startPurchase({ bundleId, idempotencyKey });
      window.location.href = response.checkout_url;
    } catch (err) {
      setError(err.message || 'Purchase failed.');
      setPurchaseLoading(false);
    }
  }

  async function loadMoreTransactions() {
    if (!nextToken || historyLoading) {
      return;
    }

    setHistoryLoading(true);
    setError('');

    try {
      const payload = await accountApi.getTransactions({
        page: historyPage + 1,
        page_size: 10,
        sort: sortOrder,
        status: statusFilter,
        next_token: nextToken,
      });
      setTransactions((prev) => [...prev, ...(payload.items || [])]);
      setNextToken(payload.next_token || null);
      setHistoryPage((prev) => prev + 1);
    } catch (err) {
      setError(err.message || 'Failed to load more history.');
    } finally {
      setHistoryLoading(false);
    }
  }

  if (authLoading) {
    return (
      <div className="account-centered-state" role="status" aria-live="polite">
        Loading account...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/visualizer" replace />;
  }

  return (
    <div className="account-page">
      <Header />
      <main className="account-main container" id="main-content" tabIndex={-1}>
        <section className="account-hero" aria-labelledby="account-heading">
          <h1 id="account-heading">My Account</h1>
          <p className="account-muted">Manage your credits, purchases, and billing history.</p>
        </section>

        {error && (
          <div className="account-alert error" role="alert">
            {error}
          </div>
        )}

        {receipt && (
          <section className="account-alert success" aria-live="polite">
            <h2>Payment confirmed</h2>
            <p>
              Receipt {receipt.receipt_number} for {formatCurrency(receipt.amount, receipt.currency, currentLocale)}
              {' '}is ready.
            </p>
            <div className="receipt-actions">
              <a className="account-secondary-button" href={receipt.download_url}>
                Download receipt
              </a>
              <button className="account-secondary-button" type="button">
                Email receipt
              </button>
            </div>
          </section>
        )}

        <section className="account-grid" aria-busy={loading}>
          <article className="account-card">
            <h2>Current Balance</h2>
            <p className="credit-balance">{account?.credits ?? 0}</p>
            <p className="account-muted">credits available</p>
            <div className="balance-summary">
              <div>
                <span className="account-muted">Recent purchases</span>
                <strong>{account?.summary?.recent_purchases ?? 0}</strong>
              </div>
              <div>
                <span className="account-muted">Recent spend</span>
                <strong>
                  {formatCurrency(account?.summary?.total_spent_recent || 0, account?.summary?.currency, currentLocale)}
                </strong>
              </div>
            </div>
          </article>

          <PurchaseCreditsWidget
            onPurchase={handlePurchase}
            isLoading={purchaseLoading}
            locale={currentLocale}
          />
        </section>

        <section className="account-card" aria-labelledby="history-heading">
          <div className="history-header">
            <h2 id="history-heading">Credit & Purchase History</h2>
            <div className="history-controls">
              <label>
                Status
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  aria-label="Filter by status"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Sort
                <select
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value)}
                  aria-label="Sort transactions"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="history-table-wrapper" role="region" aria-label="Transaction history" tabIndex={0}>
            <table className="history-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Description</th>
                  <th scope="col">Credits</th>
                  <th scope="col">Balance</th>
                  <th scope="col">Payment Method</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {!loading && visibleTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-state">
                      No transactions found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  visibleTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{formatDate(transaction.created_at, currentLocale)}</td>
                      <td>{transaction.description || transaction.type}</td>
                      <td className={transaction.credits_change >= 0 ? 'positive' : 'negative'}>
                        {transaction.credits_change >= 0 ? '+' : ''}
                        {transaction.credits_change}
                      </td>
                      <td>{transaction.balance_after}</td>
                      <td>{transaction.payment_method || 'n/a'}</td>
                      <td>
                        <span className={`status-pill ${transaction.status || 'unknown'}`}>
                          {transaction.status || 'unknown'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-row">
            <button
              type="button"
              className="account-secondary-button"
              onClick={loadMoreTransactions}
              disabled={!nextToken || historyLoading}
            >
              {historyLoading ? 'Loading...' : nextToken ? 'Load more' : 'No more records'}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default AccountPage;
